import React, { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "@/lib/api";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TOUR_OPTIONS = [
  "Golden Triangle Classic",
  "Golden Triangle Express (3 days)",
  "Golden Triangle + Ranthambore",
  "Spiritual India (Varanasi & Rishikesh)",
  "Char Dham Yatra",
  "Diwali Festival Tour",
  "Holi Festival Tour",
  "Wildlife Tiger Safari",
  "Family India Discovery",
  "Rajasthan Heritage Grand Tour",
  "South India & Kerala",
  "Kashmir Paradise",
  "Leh Ladakh Adventure",
  "Honeymoon Romance Tour",
  "Same Day Taj Mahal Tour",
  "Nepal Extension",
  "Dubai Extension",
  "Thailand Extension",
  "Custom Itinerary",
];

export default function InquiryDialog({ open, onOpenChange, defaultTour }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    tour_interest: defaultTour || "",
    travelers: "2",
    message: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (defaultTour) setForm((f) => ({ ...f, tour_interest: defaultTour }));
  }, [defaultTour]);

  const updateField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        travelers: parseInt(form.travelers, 10) || 2,
        travel_date: date ? format(date, "yyyy-MM-dd") : null,
      };
      const { data } = await api.post("/inquiries", payload);
      toast.success(data.message || "Inquiry received!");
      onOpenChange(false);
      setForm({
        name: "",
        email: "",
        phone: "",
        country: "",
        tour_interest: defaultTour || "",
        travelers: "2",
        message: "",
      });
      setDate(null);
    } catch (err) {
      toast.error(formatErrorDetail(err.response?.data?.detail) || "Failed to submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-cream border-cream-200" data-testid="inquiry-dialog">
        <DialogHeader>
          <div className="eyebrow mb-2">Plan Your Journey</div>
          <DialogTitle className="font-serif text-3xl tracking-tight text-deepred">
            Let's craft your India story
          </DialogTitle>
          <DialogDescription className="text-foreground/70">
            Share a few details and our travel curator will respond within 24 hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2" data-testid="inquiry-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                data-testid="inquiry-name"
                required
                value={form.name}
                onChange={updateField("name")}
                placeholder="Jane Doe"
                className="bg-white"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="inquiry-email"
                value={form.email}
                onChange={updateField("email")}
                placeholder="jane@example.com"
                className="bg-white"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Phone (with country code) *</Label>
              <Input
                id="phone"
                required
                data-testid="inquiry-phone"
                value={form.phone}
                onChange={updateField("phone")}
                placeholder="+44 7700 900123"
                className="bg-white"
              />
            </div>
            <div>
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                data-testid="inquiry-country"
                value={form.country}
                onChange={updateField("country")}
                placeholder="United Kingdom"
                className="bg-white"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Tour Interest</Label>
              <Select value={form.tour_interest} onValueChange={updateField("tour_interest")}>
                <SelectTrigger data-testid="inquiry-tour" className="bg-white">
                  <SelectValue placeholder="Choose a tour" />
                </SelectTrigger>
                <SelectContent>
                  {TOUR_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Travel Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="inquiry-date-trigger"
                    className="w-full h-10 px-3 py-2 bg-white border border-input rounded-md text-sm text-left flex items-center justify-between"
                  >
                    {date ? format(date, "MMM d, yyyy") : <span className="text-muted-foreground">Pick a date</span>}
                    <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="travelers">Travelers</Label>
              <Select value={form.travelers} onValueChange={updateField("travelers")}>
                <SelectTrigger data-testid="inquiry-travelers" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} {n === 1 ? "person" : "people"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="message">Tell us about your dream trip</Label>
            <Textarea
              id="message"
              data-testid="inquiry-message"
              value={form.message}
              onChange={updateField("message")}
              placeholder="Trip duration, special interests (yoga, food, photography), budget..."
              rows={4}
              className="bg-white"
            />
          </div>

          <button
            type="submit"
            data-testid="inquiry-submit"
            disabled={submitting}
            className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Send Inquiry"}
          </button>
          <p className="text-xs text-center text-muted-foreground">
            We respect your privacy. We will only use your details to respond to this inquiry.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
